import { useReducer, useEffect } from 'react';
import { isEmpty } from '../../../../helpers/utils';
import { isSSR } from '@front10/utils';

const states = {
  INIT: 'INIT',
  LOADING: 'LOADING',
  COMPLETE: 'COMPLETE',
  WAITING_FOR_DESTINATION: 'WAITING_FOR_DESTINATION',
  ERROR: 'ERROR'
};
const transitions = {
  [states.INIT]: {
    loading: states.LOADING
  },
  [states.LOADING]: {
    success: states.COMPLETE,
    error: states.ERROR
    // wait_for_destination: states.WAITING_FOR_DESTINATION
  },
  [states.WAITING_FOR_DESTINATION]: {
    load: states.LOADING,
    loading: states.LOADING,
    success: states.COMPLETE,
    wait_for_destination: states.WAITING_FOR_DESTINATION
  },
  [states.ERROR]: {
    loading: states.LOADING,
    retry: states.LOADING,
    wait_for_destination: states.WAITING_FOR_DESTINATION
  },
  [states.COMPLETE]: {
    loading: states.LOADING,
    wait_for_destination: states.WAITING_FOR_DESTINATION
  }
};

function reducer(prev, action) {
  const nextState = transitions[prev.value][action.transition];
  if (nextState) {
    return {
      ...prev,
      value: nextState,
      history: prev.value,
      ...action.payload
    };
  }
  return prev;
}

function useQuery(
  { getData, fetchData = false, firstLoading, setFetchData, setFirstLoading },
  deps = []
) {
  useEffect(() => {
    if (!fetchData && !firstLoading) {
      return;
    }
    getData();
    setFetchData(false);
    setFirstLoading(false);
    // eslint-disable-next-line
  }, [fetchData, firstLoading, ...deps]);
}

export function useTravel({
  selectedOrigin,
  selectedDestination,
  iosCountry,
  dateRange,
  flightType,
  fullyVaccinated,
  prePopulation,
  meta,
  pageContext,
  integrationSettings,
  getTravelsRestrictions,
  fetchData,
  setFetchData,
  firstLoading,
  isVisible,
  setFirstLoading,
  setIsFetchData
}) {
  let initialState = states.INIT;
  const [{ value: state, data: travelsInfo, history }, dispatch] = useReducer(reducer, {
    value: initialState,
    data: {
      arrive: {},
      returning: {},
      summaryDeparture: { icon: '', msg: '' },
      summaryReturn: { icon: '', msg: '' },
      country: { outbound: {} }
    },
    history: undefined
  });

  function match(stateValue) {
    if (isSSR() && stateValue === 'SKELETON') {
      return true;
    }
    return stateValue === state || (state === states.LOADING && history === stateValue);
  }

  useEffect(() => {
    if (
      (!selectedOrigin.value || !selectedDestination.value || isEmpty(dateRange)) &&
      transitions[state].wait_for_destination
    ) {
      dispatch({
        transition: 'wait_for_destination',
        payload: {
          data: null
        }
      });
    }
  }, [dateRange, selectedOrigin.value, selectedDestination.value, state]);

  const getData = () => {
    if (transitions[state].loading) {
      dispatch({ transition: 'loading' });
    }
    if (
      (fetchData || firstLoading) &&
      isVisible &&
      selectedOrigin &&
      selectedDestination &&
      !isEmpty(dateRange)
    ) {
      return getTravelsRestrictions(
        {
          ...prePopulation,
          origin: selectedOrigin,
          destination: selectedDestination,
          iosCountry,
          dateRange,
          flightType,
          fullyVaccinated
        },
        meta,
        pageContext,
        integrationSettings
      )
        .then((_data) => {
          dispatch({ transition: 'success', payload: { data: _data } });
          setIsFetchData(true);
        })
        .catch((err) => {
          dispatch({ transition: 'error' });
          console.error(err);
          throw err;
        });
    }
  };

  useQuery({ getData, fetchData, firstLoading, setFetchData, setFirstLoading }, []);

  return {
    travelsInfo,
    match,
    retry: () => getData()
  };
}
